import { httpClient } from '@/core/api/http-client'
import type { Paged } from '@/domain/entities/paging'
import type {
  StockAdjustmentInput,
  StockCardParams,
  StockMovement,
} from '@/domain/entities/stock-adjustment.entity'
import type { StockRepository } from '@/domain/repositories/stock.repository'
import { unwrap, unwrapPaged } from '@/infrastructure/shared/unwrap'

export interface StockMovementDto {
  id: string
  business_date: string
  ingredient_id: string
  ingredient_name?: string
  unit_name?: string
  kind: string
  qty_base: string
  cost: number
  cost_estimated: boolean
  ref_type: string
  ref_id?: string | null
  note: string
  created_by: string
  created_by_name?: string
  created_at?: string
}

function mapStockMovement(dto: StockMovementDto): StockMovement {
  return {
    id: dto.id,
    businessDate: dto.business_date,
    ingredientId: dto.ingredient_id,
    ingredientName: dto.ingredient_name,
    unitName: dto.unit_name,
    kind: dto.kind,
    qtyBase: dto.qty_base,
    cost: dto.cost,
    costEstimated: dto.cost_estimated,
    refType: dto.ref_type,
    refId: dto.ref_id,
    note: dto.note,
    createdBy: dto.created_by,
    createdByName: dto.created_by_name,
    createdAt: dto.created_at,
  }
}

export class StockHttpRepository implements StockRepository {
  async adjust(outletId: string, input: StockAdjustmentInput): Promise<StockMovement> {
    const raw = await httpClient.post<unknown>('/stock-adjustments', {
      outlet_id: outletId,
      ingredient_id: input.ingredientId,
      kind: input.kind,
      qty: input.qty,
      unit_id: input.unitId,
      positive: input.positive,
      note: input.note,
    })
    return mapStockMovement(unwrap<StockMovementDto>(raw))
  }

  async getCard(
    outletId: string,
    ingredientId?: string,
    params?: StockCardParams,
  ): Promise<Paged<StockMovement>> {
    const url =
      ingredientId && ingredientId !== 'all' ? `/inventory/card/${ingredientId}` : '/inventory/card'
    const raw = await httpClient.get<unknown>(url, {
      params: {
        outlet_id: outletId,
        page: params?.page,
        limit: params?.limit,
        kind: params?.kind || undefined,
        search: params?.search || undefined,
        from: params?.from || undefined,
        to: params?.to || undefined,
      },
    })
    const paged = unwrapPaged<StockMovementDto>(raw, params?.page ?? 1, params?.limit ?? 50)
    return { ...paged, items: paged.items.map(mapStockMovement) }
  }
}
